/**
 * Jitsi helper utilities for the web portal.
 * Matches the iOS OnlineClass model's Jitsi room naming and URL generation.
 */

export const JITSI_SERVER_HOST = 'meet.jit.si';

/**
 * Generates a stable, unique Jitsi room name for an online class session.
 * Matches OnlineClass.jitsiRoomName on iOS.
 * @param {string} onlineClassId - The UUID of the online class
 * @returns {string} - The Jitsi room name (e.g., 'kn-edu-portal-abc123')
 */
export function getJitsiRoomName(onlineClassId) {
  if (!onlineClassId) return '';
  const raw = `kn-edu-portal-${onlineClassId.toLowerCase()}`;
  return raw.replace(/ /g, '-');
}

/**
 * Generates the full Jitsi meeting URL for a session.
 * Matches OnlineClass.jitsiURL on iOS.
 * @param {string} onlineClassId - The UUID of the online class
 * @returns {string} - The full Jitsi meeting URL
 */
export function getJitsiURL(onlineClassId) {
  const roomName = getJitsiRoomName(onlineClassId);
  return `https://${JITSI_SERVER_HOST}/${roomName}`;
}

/**
 * Determines whether an online class doc is actually online/hybrid.
 * iOS stores `classMode` ("In-Person" | "Online" | "Hybrid") — `isOnline` is a
 * Swift computed property and is NEVER written to Firestore, so we derive it
 * from classMode. Falls back to a stored `isOnline` boolean or a stored
 * `jitsiRoomName` for legacy web-created docs.
 * @param {object} onlineClass - The online class object
 * @returns {boolean}
 */
export function isOnlineClassMode(onlineClass) {
  if (!onlineClass) return false;
  const mode = (onlineClass.classMode || '').toLowerCase();
  if (mode === 'online' || mode === 'hybrid') return true;
  if (mode === 'in-person' || mode === 'inperson') return false;
  // Legacy/web docs without classMode: honor stored flags if present.
  if (onlineClass.isOnline === true || onlineClass.isLive === true) return true;
  return false;
}

/**
 * Determines the preferred join URL for an online class.
 * If an explicit external meetingURL was set (e.g., Zoom/Meet), that is used.
 * Otherwise, the auto-generated Jitsi room URL is used.
 * Matches OnlineClass.preferredJoinURL on iOS.
 * Completed/cancelled classes are never joinable.
 * @param {object} onlineClass - The online class object
 * @returns {string|null} - The URL to join, or null if not joinable
 */
export function getPreferredJoinURL(onlineClass) {
  if (!onlineClass) return null;

  const status = (onlineClass.status || '').toLowerCase();
  if (status === 'completed' || status === 'cancelled') return null;

  if (onlineClass.meetingURL) {
    try {
      const url = new URL(onlineClass.meetingURL);
      if (url.protocol) {
        return onlineClass.meetingURL;
      }
    } catch (e) {
      // Invalid URL, fall through to Jitsi
    }
  }

  if (isOnlineClassMode(onlineClass) || onlineClass.jitsiRoomName) {
    return getJitsiURL(onlineClass.id);
  }
  return null;
}
